import { createSlice } from '@reduxjs/toolkit';
import { bulkDealArchiveThunk, bulkDealDeleteThunk, bulkDealTransferThunk, createPipelineThunk, deletePipelineThunk, duplicatePipelineThunk, editPipelineThunk, fetchPipelineTemplatesThunk, getAllPipelinesThunk, getSinglePipelineDetailThunk } from '../thunks/pipeline';
import { PipelineObject, PipelineState, PipelineTemplateObject, StageObject } from '../../params/redux/pipeline';

// Initial state with default values for authentication and user info
const initialState: PipelineState = {
    // Auth token stored after successful login
    pipelines: [],
    selectedPipeline: null,
    bulkEditMode: false,
    selectedDeals: [],
    selectedStages: [],
    selectedStageForBulkEdit: 0,
    templates: [],
    loaders: {
        isCreating: false,
        isUpdating: false,
        isDeleting: false,
        isLoading: false,
        isSearching: false,
        isInserting: false
    }
};

export const filteredDeals = (stage: StageObject) => stage.deals.filter((deal) => deal.status !== 'cancelled' && deal.is_archieved !== 1 && deal.is_deleted !== 1);

// Slice for app-related states and actions
const pipelineSlice = createSlice({
    // Slice name
    name: 'pipeline',
    // inital state for the slice
    initialState,
    reducers: {
        settingSelectedPipeline: (state, action) => {
            if (action.payload === null) {
                state.selectedPipeline = null;
            } else {
                const pipeline = action.payload as PipelineObject;
                pipeline.pipeline_stages = (pipeline.pipeline_stages || []).map((stage) => ({
                    ...stage,
                    deals: filteredDeals(stage)
                }));
                state.selectedPipeline = pipeline;
            }
        },
        toggleBulkEditMode: (state, action) => {
            state.bulkEditMode = action.payload;
            if (!action.payload) {
                state.selectedDeals = [];
                state.selectedStages = [];
            }
        },

        selectAllDealsForBulkEdit: (state) => {
            if (!state.selectedPipeline) return;
            const totalDeals = state.selectedPipeline?.pipeline_stages?.flatMap((stage) => stage.deals).length || 0;
            if (state.selectedDeals.length === totalDeals) {
                state.selectedDeals = [];
                state.selectedStages = [];
            } else {
                const allDeals = state.selectedPipeline.pipeline_stages?.flatMap((stage) => stage.deals) || [];
                const allStages = state.selectedPipeline.pipeline_stages?.filter((stage) => stage.deals.length > 0).map((stage) => stage.id) || [];
                state.selectedDeals = allDeals.map((deal) => deal.id);
                state.selectedStages = allStages;
            }
        },

        selectDealForBulkEdit: (state, action) => {
            const dealId = action.payload;
            const selectedDealIndex = state.selectedDeals.indexOf(dealId);

            if (selectedDealIndex > -1) {
                // If already selected, deselect it
                state.selectedDeals = state.selectedDeals.filter((id) => id !== dealId);
            } else {
                // If not selected, add it
                state.selectedDeals.push(dealId);
            }

            // Find the stage of this deal
            const dealStage = state.selectedPipeline?.pipeline_stages?.find((stage) => stage.deals.some((deal) => deal.id === dealId));

            if (dealStage) {
                const stageDeals = dealStage.deals.map((deal) => deal.id);
                const allStageDealsSelected = stageDeals.every((id) => state.selectedDeals.includes(id));

                // If all deals in stage are selected, select the stage
                if (allStageDealsSelected) {
                    if (!state.selectedStages.includes(dealStage.id)) {
                        state.selectedStages.push(dealStage.id);
                    }
                } else {
                    // If any deal is deselected, remove stage selection
                    state.selectedStages = state.selectedStages.filter((stageId) => stageId !== dealStage.id);
                }
            }
        },

        selectStageForBulkEdit: (state, action) => {
            const stageId = action.payload;
            const stage = state.selectedPipeline?.pipeline_stages?.find((s) => s.id === stageId);

            if (!stage) return;

            const stageDealIds = stage.deals.map((deal) => deal.id);
            const isStageSelected = state.selectedStages.includes(stageId);

            if (isStageSelected) {
                // If stage is already selected, deselect it and remove all its deals
                state.selectedStages = state.selectedStages.filter((id) => id !== stageId);
                state.selectedDeals = state.selectedDeals.filter((dealId) => !stageDealIds.includes(dealId));
            } else {
                // If stage is not selected, select it and add all its deals
                state.selectedStages.push(stageId);
                state.selectedDeals = Array.from(new Set([...state.selectedDeals, ...stageDealIds]));
            }
        },
        resetBulkSelection: (state) => {
            state.selectedDeals = [];
            state.selectedStages = [];
        },

        settingSelectedStageForBulkEdit: (state, action) => {
            state.selectedStageForBulkEdit = action.payload;
        },
        // Resets loading states related to app actions
        resettingPipelineLoader: (state) => {
            state.loaders = initialState.loaders;
        },
        // Resets all app data to the initial state
        resettingPipelineData: () => {
            return initialState;
        }
    },
    extraReducers: (builder) => {
        // Handle async actions for login
        builder
            .addCase(getAllPipelinesThunk.pending, (state) => {
                // Set loading flag during login request
                state.loaders.isLoading = true;
            })
            .addCase(getAllPipelinesThunk.fulfilled, (state, action) => {
                // Update state with login success details
                state.pipelines = action.payload?.pipelines as PipelineObject[];
            })
            .addCase(getAllPipelinesThunk.rejected, (state) => {
                state.pipelines = [];
                state.selectedPipeline = null;
            });
        builder
            .addCase(createPipelineThunk.pending, (state) => {
                state.loaders.isCreating = true;
            })
            .addCase(createPipelineThunk.fulfilled, () => {
                //
            })
            .addCase(createPipelineThunk.rejected, () => {
                //
            });
        builder
            .addCase(getSinglePipelineDetailThunk.pending, (state) => {
                state.loaders.isLoading = true;
            })
            .addCase(getSinglePipelineDetailThunk.fulfilled, (state, action) => {
                const pipeline = action.payload as PipelineObject;

                // Ensure the selected pipeline contains filtered deals for each stage
                if (pipeline.pipeline_stages) {
                    pipeline.pipeline_stages = pipeline.pipeline_stages.map((stage) => ({
                        ...stage,
                        deals: filteredDeals(stage)
                    })).sort((a, b) => a.sort_order - b.sort_order);
                }

                state.selectedPipeline = pipeline;
            })
            .addCase(getSinglePipelineDetailThunk.rejected, (state) => {
                state.pipelines = [];
                state.selectedPipeline = null;
            });
        builder
            .addCase(editPipelineThunk.pending, (state) => {
                state.loaders.isUpdating = true;
            })
            .addCase(editPipelineThunk.fulfilled, () => {
                //
            })
            .addCase(editPipelineThunk.rejected, () => {
                //
            });
        builder
            .addCase(deletePipelineThunk.pending, (state) => {
                state.loaders.isDeleting = true;
            })
            .addCase(deletePipelineThunk.fulfilled, () => {
                //
            })
            .addCase(deletePipelineThunk.rejected, () => {
                //
            });
        builder
            .addCase(duplicatePipelineThunk.pending, (state) => {
                state.loaders.isLoading = true;
            })
            .addCase(duplicatePipelineThunk.fulfilled, () => {
                //
            })
            .addCase(duplicatePipelineThunk.rejected, () => {
                //
            });
        builder
            .addCase(bulkDealArchiveThunk.pending, (state) => {
                state.loaders.isLoading = true;
            })
            .addCase(bulkDealArchiveThunk.fulfilled, (state) => {
                state.bulkEditMode = false;
                state.selectedDeals = [];
                state.selectedStages = [];
                state.selectedStageForBulkEdit = 0;
            })
            .addCase(bulkDealArchiveThunk.rejected, (state) => {
                state.bulkEditMode = false;
                state.selectedDeals = [];
                state.selectedStages = [];
                state.selectedStageForBulkEdit = 0;
            });
        builder
            .addCase(bulkDealDeleteThunk.pending, (state) => {
                state.loaders.isLoading = true;
            })
            .addCase(bulkDealDeleteThunk.fulfilled, (state) => {
                state.bulkEditMode = false;
                state.selectedDeals = [];
                state.selectedStages = [];
                state.selectedStageForBulkEdit = 0;
            })
            .addCase(bulkDealDeleteThunk.rejected, (state) => {
                state.bulkEditMode = false;
                state.selectedDeals = [];
                state.selectedStages = [];
                state.selectedStageForBulkEdit = 0;
            });
        builder
            .addCase(bulkDealTransferThunk.pending, (state) => {
                state.loaders.isLoading = true;
            })
            .addCase(bulkDealTransferThunk.fulfilled, (state) => {
                state.bulkEditMode = false;
                state.selectedDeals = [];
                state.selectedStages = [];
                state.selectedStageForBulkEdit = 0;
            })
            .addCase(bulkDealTransferThunk.rejected, (state) => {
                state.bulkEditMode = false;
                state.selectedDeals = [];
                state.selectedStages = [];
                state.selectedStageForBulkEdit = 0;
            });
            builder
            .addCase(fetchPipelineTemplatesThunk.pending, (state) => {
                state.loaders.isLoading = true;
            })
            .addCase(fetchPipelineTemplatesThunk.fulfilled, (state, action) => {
                state.templates = action.payload as PipelineTemplateObject[];
            })
            .addCase(fetchPipelineTemplatesThunk.rejected, (state) => {
                state.templates = [];
            });
    }
});

// Exporting actions for updating app state
export const {
    selectAllDealsForBulkEdit,
    settingSelectedStageForBulkEdit,
    toggleBulkEditMode,
    selectDealForBulkEdit,
    selectStageForBulkEdit,
    resetBulkSelection,
    settingSelectedPipeline,
    resettingPipelineData,
    resettingPipelineLoader
} = pipelineSlice.actions;

// Exporting the reducer to be used in the store
export default pipelineSlice.reducer;
